import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../i18n';
import { Portal } from '../Portal';
import { CheckIcon, ExclamationTriangleIcon, XMarkIcon } from '../../storage/icons';

/**
 * Système de NOTIFICATIONS « toaster » (source unique des messages transitoires).
 *
 * Un toast porte une TONALITÉ (`success` = vert, `error` = rouge — jamais l'info par la
 * couleur seule : icône + texte), une durée par défaut de {@link DEFAULT_DURATION} ms,
 * une BARRE DE PROGRESSION qui mesure le temps restant, un bouton de fermeture, et
 * ÉVENTUELLEMENT une {@link ToastAction} (bouton discret — « Annuler » d'une suppression,
 * XFILE-33). Survoler un toast MET EN PAUSE son minuteur et sa barre.
 *
 * a11y : région dédiée `aria-label`, chaque toast `role="status"` (succès, poli) ou
 * `role="alert"` (erreur, assertif). Le `useToast` hors provider retombe sur un NO-OP
 * (aucun plantage en test unitaire isolé) ; le vrai provider est monté à la racine.
 */

export type ToastKind = 'success' | 'error';

/**
 * ACTION optionnelle d'un toast : un bouton unique, rendu avant la croix de fermeture. Le
 * déclencher FERME le toast (l'action est consommée une fois — pas de double annulation).
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

/**
 * Options d'affichage. Un simple `number` reste accepté — c'est la forme HISTORIQUE (durée
 * seule) : l'extension est ADDITIVE, aucun appel existant n'a besoin d'être retouché.
 */
export type ToastOptions = number | { duration?: number; action?: ToastAction };

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  duration: number;
  action?: ToastAction;
}

interface ToastApi {
  show: (kind: ToastKind, message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

/** Durée d'affichage par défaut (4 secondes). */
export const DEFAULT_DURATION = 4000;

/**
 * Durée par défaut d'un toast PORTEUR D'ACTION (8 secondes) : il faut le temps de LIRE puis de
 * CLIQUER. Un « Annuler » qui s'évapore en 4 s est un « Annuler » qu'on rate.
 */
export const ACTION_DURATION = 8000;

/** Normalise les options (forme historique `number` incluse) en `{ duration, action }`. */
function normalizeOptions(options: ToastOptions | undefined): {
  duration: number;
  action?: ToastAction;
} {
  if (typeof options === 'number') return { duration: options };
  const action = options?.action;
  return {
    duration: options?.duration ?? (action ? ACTION_DURATION : DEFAULT_DURATION),
    ...(action && { action }),
  };
}

const noop: ToastApi = {
  show: () => {},
  success: () => {},
  error: () => {},
  dismiss: () => {},
};

const ToastContext = createContext<ToastApi>(noop);

/** Accès à l'API de notifications. Sûr hors provider (retombe sur un no-op). */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/** Compteur monotone d'identifiants de toasts (clés React stables et uniques). */
let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((kind: ToastKind, message: string, options?: ToastOptions): void => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, kind, message, ...normalizeOptions(options) }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show('success', message, options),
      error: (message, options) => show('error', message, options),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <Portal>
          <ToastViewport toasts={toasts} onDismiss={dismiss} />
        </Portal>
      )}
    </ToastContext.Provider>
  );
}

/** Pile de toasts, ancrée en bas (centrée en mobile, à droite dès `sm`). */
function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div
      role="region"
      aria-label={t('toast.region')}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/** Un toast : icône + message + (action) + bouton fermer, avec barre de progression pausable. */
function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const { id, kind, message, duration, action } = toast;
  const [paused, setPaused] = useState(false);

  // Temps restant reporté d'une reprise à l'autre : chaque pause soustrait l'écoulé.
  const remainingRef = useRef(duration);
  const startRef = useRef(0);

  useEffect(() => {
    if (paused) return;
    startRef.current = performance.now();
    const timer = window.setTimeout(() => onDismiss(id), remainingRef.current);
    return () => {
      window.clearTimeout(timer);
      remainingRef.current -= performance.now() - startRef.current;
    };
  }, [paused, id, onDismiss]);

  const success = kind === 'success';

  return (
    <div
      role={success ? 'status' : 'alert'}
      aria-live={success ? 'polite' : 'assertive'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border bg-surface shadow-lg ${
        success ? 'border-success-ink' : 'border-danger'
      }`}
    >
      <div className="flex items-start gap-3 p-3">
        <span className={`mt-0.5 shrink-0 ${success ? 'text-success-ink' : 'text-danger'}`}>
          {success ? (
            <CheckIcon className="size-5" />
          ) : (
            <ExclamationTriangleIcon className="size-5" />
          )}
        </span>
        <p className="min-w-0 flex-1 text-sm text-fg">{message}</p>
        {/* Action optionnelle (« Annuler ») : bouton DISCRET, avant la croix. La déclencher
            ferme le toast — l'occasion ne se représente pas, on ne l'offre pas deux fois. */}
        {action && (
          <button
            type="button"
            onClick={() => {
              onDismiss(id);
              action.onClick();
            }}
            className="-my-0.5 shrink-0 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            {action.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(id)}
          aria-label={t('toast.dismiss')}
          className="-me-1 -mt-1 shrink-0 rounded-md p-1 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <XMarkIcon className="size-4" />
        </button>
      </div>
      {/* Barre d'état : mesure le temps restant. Pause au survol/focus (play-state). */}
      <div className="h-1 bg-surface-2" aria-hidden="true">
        <div
          className={`h-full ${success ? 'bg-success' : 'bg-danger'}`}
          style={{
            animation: `toast-progress ${duration}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      </div>
    </div>
  );
}
